// exam.js: assembles a full mock exam from the flattened by-section pool (generated by
// scripts/build-registry.js) on the real PPL blueprint (PLAN §5.2), and scores
// it against the real pass rules (60% overall AND 60% in every section). Pure: the whole exam
// is a function of one master seed, so it can be replayed or shared by seed (PLAN §5, §9).

import { composeExam } from './compose.js';
import { BLUEPRINT } from './blueprint.js';
import { SYLLABUS } from './syllabus.js';
import { GENERATORS } from '../../_generated/exam-pool.js';

/** @typedef {import('./contract.js').Question} Question */

export const PASS_PCT = 60;

/**
 * Build the mock exam for a master seed: blueprint sections in order, each drawn by
 * syllabus-weighted composition (compose.js). Replays by seed when `recent` is the same.
 * @param {number} masterSeed
 * @param {Set<string>} [recent] recently-drawn lesson topics to de-prioritize
 * @returns {{ seed: number, questions: Question[] }}
 */
export const buildExam = (masterSeed, recent) => ({
  seed: masterSeed,
  questions: composeExam(GENERATORS, BLUEPRINT, SYLLABUS, masterSeed, recent),
});

/**
 * Score an exam. `answers[i]` is the chosen option index for question i (null = unanswered).
 * @param {Question[]} questions
 * @param {(number|null)[]} answers
 * @returns {{ correct:number, total:number, overallPct:number, pass:boolean,
 *   sections: Record<string,{correct:number,total:number,pct:number,pass:boolean}> }}
 */
export function scoreExam(questions, answers) {
  const sections = {};
  questions.forEach((q, i) => {
    const s = (sections[q.section] ??= { correct: 0, total: 0, pct: 0, pass: false });
    s.total++;
    if (answers[i] === q.answer) s.correct++;
  });
  const pct = (c, t) => (t ? (100 * c) / t : 0);
  let correct = 0;
  for (const s of Object.values(sections)) {
    correct += s.correct;
    s.pct = pct(s.correct, s.total);
    s.pass = s.pct >= PASS_PCT;
  }
  const total = questions.length;
  const overallPct = pct(correct, total);
  const pass = overallPct >= PASS_PCT && Object.values(sections).every((s) => s.pass);
  return { correct, total, overallPct, pass, sections };
}
